import { useState } from "react";
import { api, ApiError } from "../net/api.js";
import { authErrorTranslationKey } from "../net/auth-errors.js";
import { refreshSession } from "../net/session.js";
import { Button, ErrorNote, Panel } from "../ui/primitives.js";
import { TurnstileWidget, useTurnstilePolicy } from "../ui/Turnstile.js";
import { useLocale } from "../i18n.js";

/**
 * Turn the guest you already are into an account.
 *
 * The same two steps as registration — address, then code and password — but
 * asked of someone who is already signed in and already has something to lose,
 * so the copy is about keeping a level rather than creating an identity. The
 * account id never changes, which is why this can promise that the matches
 * already played stay attached to it.
 */
export function ClaimAccount() {
  const { locale, t } = useLocale();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captchaRequired = useTurnstilePolicy();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    // Only the code request is challenged; by the code step the address has
    // already been proven reachable.
    const needsCaptcha = step === "email" && captchaRequired !== false;
    if (needsCaptcha && captchaToken === null) {
      setError(captchaRequired === null ? t("captchaUnavailable") : t("captchaRequired"));
      return;
    }
    setBusy(true);
    try {
      if (step === "email") {
        const result = await api.registerCode(email.trim(), locale, captchaToken ?? undefined);
        setDevCode(result.devCode ?? null);
        setStep("code");
        return;
      }
      if (password !== confirm) { setError(t("passwordMismatch")); return; }
      await api.claimGuest(email.trim(), code.trim(), password, locale);
      await refreshSession();
    } catch (caught) {
      setError(caught instanceof ApiError
        ? t(authErrorTranslationKey(caught.code, caught.status, step === "code"))
        : t("authServiceUnavailable"));
    } finally {
      setBusy(false);
      setCaptchaToken(null);
      setCaptchaResetKey((key) => key + 1);
    }
  }

  return (
    <Panel className="claim-account">
      <div className="profile-section-heading">
        <div>
          <h2 className="section-title">{t("guestKeepTitle")}</h2>
          <p className="section-hint">{t("guestKeepHint")}</p>
        </div>
      </div>
      <form onSubmit={submit}>
        <label className="field">
          <span className="field-label">{t("email")}</span>
          <input
            className="field-input" type="email" inputMode="email" autoComplete="email" required
            value={email} disabled={step === "code"} placeholder={t("emailPlaceholder")}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {step === "code" && <>
          <label className="field">
            <span className="field-label">{t("codeSent", { email })}</span>
            <input
              className="field-input field-code tabular" type="text" inputMode="numeric"
              autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required autoFocus
              value={code} placeholder="000000"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </label>
          <label className="field">
            <span className="field-label">{t("newPassword")}</span>
            <input className="field-input" type="password" autoComplete="new-password" required minLength={8} maxLength={72} value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">{t("confirmPassword")}</span>
            <input className="field-input" type="password" autoComplete="new-password" required minLength={8} maxLength={72} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </label>
        </>}
        {step === "email" && captchaRequired !== false && (
          <TurnstileWidget enabled={captchaRequired === true} resetKey={captchaResetKey} onToken={setCaptchaToken} />
        )}
        {devCode !== null && (
          <button type="button" className="devcode" onClick={() => setCode(devCode)}>{t("devCode", { code: devCode })}</button>
        )}
        <Button type="submit" variant="primary" busy={busy}>
          {step === "email" ? t("sendCode") : t("createAccount")}
        </Button>
        {error !== null && <ErrorNote>{error}</ErrorNote>}
      </form>
      <p className="section-hint">{t("guestLimits")}</p>
    </Panel>
  );
}
